'use client';

import {
    SelectedAutoRepliesModal,
    type SelectedAutoRepliesRunScope,
} from '@/components/selected-auto-replies/SelectedAutoRepliesModal';
import { createClientLogger } from '@/client/logging/client-logger';
import { createContext, type PropsWithChildren, useCallback, useContext, useMemo, useState } from 'react';

type SelectedAutoRepliesModalContextValue = {
    openSelectedAutoRepliesModal: (runScope?: SelectedAutoRepliesRunScope) => void;
};

const selectedAutoRepliesModalContextLogger = createClientLogger({
    scope: 'selected-auto-replies-modal-context',
});

const missingProviderContext: SelectedAutoRepliesModalContextValue = {
    openSelectedAutoRepliesModal: () => {
        selectedAutoRepliesModalContextLogger.warn(
            '[client.selected-auto-replies-modal.open] Skipped selected auto-replies modal open without provider.',
            {
                outcome: 'missing_provider',
            },
        );
    },
};

const SelectedAutoRepliesModalContext = createContext<SelectedAutoRepliesModalContextValue>(missingProviderContext);

export const useSelectedAutoRepliesModal = (): SelectedAutoRepliesModalContextValue =>
    useContext(SelectedAutoRepliesModalContext);

export const SelectedAutoRepliesModalProvider = ({ children }: PropsWithChildren) => {
    const [isSelectedAutoRepliesModalOpen, setIsSelectedAutoRepliesModalOpen] = useState(false);
    const [runScope, setRunScope] = useState<SelectedAutoRepliesRunScope | undefined>(undefined);

    const openSelectedAutoRepliesModal = useCallback((nextRunScope?: SelectedAutoRepliesRunScope) => {
        setRunScope(nextRunScope);
        selectedAutoRepliesModalContextLogger.info(
            '[client.selected-auto-replies-modal.open] Accepted selected auto-replies modal open request.',
            {
                outcome: 'accepted',
                selectionScope: nextRunScope?.type ?? 'selection_context',
                reviewFeedItemIdsCount:
                    nextRunScope?.type === 'reviewFeedItemIds' ? nextRunScope.reviewFeedItemIds.length : undefined,
            },
        );
        setIsSelectedAutoRepliesModalOpen(true);
    }, []);

    const setSelectedAutoRepliesModalShown = useCallback((nextIsShown: boolean) => {
        setIsSelectedAutoRepliesModalOpen(nextIsShown);
        if (!nextIsShown) {
            setRunScope(undefined);
        }
    }, []);

    const value = useMemo(
        () => ({
            openSelectedAutoRepliesModal,
        }),
        [openSelectedAutoRepliesModal],
    );

    return (
        <SelectedAutoRepliesModalContext.Provider value={value}>
            {children}
            <SelectedAutoRepliesModal
                isShown={isSelectedAutoRepliesModalOpen}
                setIsShown={setSelectedAutoRepliesModalShown}
                runScope={runScope}
            />
        </SelectedAutoRepliesModalContext.Provider>
    );
};
